/**
 * Engagement Agent — dedicated page with Overview / Logs / Settings tabs.
 * Surfaces the Pro human-interaction feature (auto-reply to comments & DMs)
 * out of Settings into its own screen with stats and a reply log.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Badge, EmptyState, Spinner, Textarea, useToast } from '../components/ui';
import { EngagementIcon } from '../components/NavIcons';
import { AgentTabs, type AgentTab } from '../components/agents/agentsCommon';
import EngagementSettings from '../components/agents/EngagementSettings';
import {
  getInteractionStatus,
  runInteractionNow,
  getInteractionEvents,
  getJobStatus,
  sendInteractionReply,
  dismissInteractionReply,
} from '../api';
import type {
  PageKey,
  InteractionStatus,
  InteractionEventLog,
  InteractionEventStatus,
  Workspace,
} from '../types';
import '../components/agents/agents.css';

interface Props {
  workspace: Workspace;
  onWorkspaceUpdate: (_ws: Workspace) => void;
  onNavigate: (_page: PageKey) => void;
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function statusVariant(s: InteractionEventStatus): 'approved' | 'error' | 'scheduled' | 'draft' {
  if (s === 'sent') return 'approved';
  if (s === 'failed') return 'error';
  if (s === 'drafted') return 'scheduled';
  return 'draft';
}

const KIND_LABEL: Record<string, string> = {
  comment: 'Comment',
  dm: 'DM',
  request: 'Request',
};

type KindFilter = '' | 'comment' | 'dm' | 'request';

export default function EngagementPage({ workspace, onWorkspaceUpdate, onNavigate }: Props) {
  const [tab, setTab] = useState<AgentTab>('overview');
  const [data, setData] = useState<InteractionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [pollJobId, setPollJobId] = useState<string | null>(null);

  const [events, setEvents] = useState<InteractionEventLog[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState<KindFilter>('');

  const canUse = (data?.is_entitled ?? workspace.has_human_interaction) ?? false;

  const fetchStatus = useCallback(async () => {
    try {
      setData(await getInteractionStatus(workspace.id));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [workspace.id]);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await getInteractionEvents(workspace.id, {
        kind: kindFilter || undefined,
        limit: 50,
      });
      setEvents(res.events);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [workspace.id, kindFilter]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (tab === 'logs') fetchEvents();
  }, [tab, fetchEvents]);

  useEffect(() => {
    if (!pollJobId) return;
    const t = setInterval(async () => {
      try {
        const job = await getJobStatus(pollJobId);
        if (job.status === 'completed' || job.status === 'failed') {
          setPollJobId(null);
          setTriggering(false);
          await fetchStatus();
          if (tab === 'logs') await fetchEvents();
        }
      } catch {
        setPollJobId(null);
        setTriggering(false);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [pollJobId, fetchStatus, fetchEvents, tab]);

  const runNow = async () => {
    setTriggering(true);
    try {
      const res = await runInteractionNow(workspace.id);
      setPollJobId(res.job_id);
      await fetchStatus();
    } catch {
      setTriggering(false);
    }
  };

  const stats = data?.stats;
  const runs = data?.recent_runs ?? [];
  const isRunning =
    !!pollJobId || runs[0]?.status === 'queued' || runs[0]?.status === 'running';

  return (
    <div className="agent-page">
      <div className="agent-hero">
        <div className="agent-hero__icon">
          <EngagementIcon size={26} />
        </div>
        <div>
          <div className="agent-hero__title">
            Engagement Agent
            {!canUse && <span className="settings-plan-lock-badge">🔒 Pro</span>}
          </div>
          <div className="agent-hero__subtitle">
            Never leave a comment or DM unanswered. The Engagement Agent reads your incoming
            messages every hour and replies in your brand voice — building community while you
            sleep.
          </div>
        </div>
        {canUse && (
          <div className="agent-hero__actions">
            <Button variant="primary" size="sm" loading={triggering} disabled={isRunning} onClick={runNow}>
              {isRunning ? 'Running…' : 'Run now'}
            </Button>
          </div>
        )}
      </div>

      <AgentTabs active={tab} onChange={setTab} />

      {loading ? (
        <Spinner size="md" />
      ) : tab === 'settings' ? (
        <EngagementSettings workspace={workspace} onWorkspaceUpdate={onWorkspaceUpdate} />
      ) : !canUse ? (
        <EmptyState
          icon={<EngagementIcon size={28} />}
          title="The Engagement Agent is a Pro feature"
          description="Upgrade to Pro to auto-reply to comments and DMs in your brand voice."
          action={
            <Button variant="primary" size="sm" onClick={() => onNavigate('settings')}>
              Upgrade to Pro
            </Button>
          }
        />
      ) : tab === 'overview' ? (
        <>
          <div className="agent-status-line">
            <span>
              Status:{' '}
              <strong>
                {data?.blocked_reason
                  ? 'Paused'
                  : data?.interaction_enabled
                    ? 'Active'
                    : 'Inactive'}
              </strong>
            </span>
            <span>
              Last run: <strong>{relTime(data?.interaction_last_run_at)}</strong>
            </span>
            {data?.interaction_enabled &&
              (data.blocked_reason ? (
                // Takes the place of "Next run" — the tick won't fire until the
                // connection is fixed, so promising a time would be a lie.
                <span className="agent-status-line__paused" title={data.blocked_reason}>
                  Instagram disconnected —{' '}
                  <button
                    className="agent-status-line__fix"
                    onClick={() => onNavigate('settings')}
                  >
                    reconnect
                  </button>
                </span>
              ) : (
                <span>
                  Next run: <strong>{relTime(data.next_run_at)}</strong>
                </span>
              ))}
          </div>
          <div className="agent-stats">
            <div className="agent-stat">
              <div className="agent-stat__value">{stats?.sent ?? 0}</div>
              <div className="agent-stat__label">Replies sent</div>
            </div>
            <div className="agent-stat">
              <div className="agent-stat__value">{stats?.sent_last_7d ?? 0}</div>
              <div className="agent-stat__label">Sent (7 days)</div>
            </div>
            <div className="agent-stat">
              <div className="agent-stat__value">{stats?.drafted ?? 0}</div>
              <div className="agent-stat__label">Drafted</div>
            </div>
            <div className="agent-stat">
              <div className="agent-stat__value">{stats?.total ?? 0}</div>
              <div className="agent-stat__label">Total handled</div>
            </div>
          </div>
        </>
      ) : (
        // Logs tab
        <>
          <div className="agent-log-filters">
            {(['', 'comment', 'dm', 'request'] as KindFilter[]).map((k) => (
              <button
                key={k || 'all'}
                className={`agent-chip${kindFilter === k ? ' agent-chip--active' : ''}`}
                onClick={() => setKindFilter(k)}
              >
                {k === '' ? 'All' : KIND_LABEL[k]}
              </button>
            ))}
          </div>
          {eventsLoading ? (
            <Spinner size="md" />
          ) : events.length === 0 ? (
            <EmptyState
              title="No interactions yet"
              description="When people comment on your posts or message you, the agent's replies will show up here."
            />
          ) : (
            <div className="agent-log-list">
              {events.map((e) => (
                <EngagementLogRow
                  key={e.id}
                  event={e}
                  workspaceId={workspace.id}
                  onUpdated={(updated) =>
                    setEvents((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EngagementLogRow({
  event: e,
  workspaceId,
  onUpdated,
}: {
  event: InteractionEventLog;
  workspaceId: number;
  onUpdated: (_e: InteractionEventLog) => void;
}) {
  const { toast } = useToast();
  // A drafted (or previously failed) reply can be reviewed, edited and sent.
  const actionable = e.status === 'drafted' || e.status === 'failed';
  const [text, setText] = useState(e.drafted_reply);
  const [busy, setBusy] = useState<null | 'send' | 'dismiss'>(null);

  useEffect(() => {
    setText(e.drafted_reply);
  }, [e.drafted_reply]);

  const doSend = async () => {
    if (!text.trim()) return;
    setBusy('send');
    try {
      onUpdated(await sendInteractionReply(workspaceId, e.id, text.trim()));
      toast('Reply sent.', 'success');
    } catch (err: any) {
      toast(err?.message ?? 'Failed to send reply.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const doDismiss = async () => {
    setBusy('dismiss');
    try {
      onUpdated(await dismissInteractionReply(workspaceId, e.id));
    } catch (err: any) {
      toast(err?.message ?? 'Failed to dismiss.', 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="agent-log-row">
      <div className="agent-log-row__main">
        <div className="agent-log-row__meta">
          <Badge variant={statusVariant(e.status)} dot>
            {e.status}
          </Badge>
          <span>{KIND_LABEL[e.kind] ?? e.kind}</span>
          {e.sender_username && <span>@{e.sender_username}</span>}
        </div>
        {e.incoming_text && <div className="agent-log-row__incoming">{e.incoming_text}</div>}

        {actionable ? (
          <div className="agent-draft-editor">
            <Textarea
              value={text}
              onChange={(ev) => setText(ev.target.value)}
              rows={2}
              placeholder="Write a reply…"
              aria-label="Draft reply"
            />
            <div className="agent-draft-actions">
              <Button size="sm" variant="primary" loading={busy === 'send'} disabled={!!busy || !text.trim()} onClick={doSend}>
                Send reply
              </Button>
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={doDismiss}>
                Dismiss
              </Button>
            </div>
            {e.last_error && <div className="agent-log-row__reply">⚠ {e.last_error}</div>}
          </div>
        ) : (
          <>
            {e.drafted_reply && <div className="agent-log-row__reply">↳ {e.drafted_reply}</div>}
            {e.last_error && <div className="agent-log-row__reply">⚠ {e.last_error}</div>}
          </>
        )}
      </div>
      <div className="agent-log-row__time">{relTime(e.sent_at || e.created_at)}</div>
    </div>
  );
}
